/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-vars */
import { GomarkePixelConfig } from '@/types';
import type { TrackConversionFn } from '../engine';
import { BaseAutoTracker } from './BaseAutoTracker';
import { AutoAddToCartTracker, IAutoAddToCartConfig } from './autoAddToCart';
import {
  AutoInitiateCheckoutTracker,
  IAutoInitiateCheckoutConfig,
} from './autoInitiateCheckout';
import { AutoLeadTracker, IAutoLeadConfig } from './autoLead';
import {
  AutoRemoveToCartTracker,
  IAutoRemoveToCartConfig,
} from './autoRemoveToCart';
import {
  AutoViewContentTracker,
  IAutoViewContentConfig,
} from './autoViewContent';
import {
  AutoAddPaymentInfoTracker,
  IAutoAddPaymentInfoConfig,
} from './autoAddPaymentInfo';
import {
  AutoCustomerTracker,
  IAutoCustomerTrackerConfig,
} from './autoCustomerTracker';
import {
  AutoCustomerAddToCartTracker,
  IAutoCustomerAddToCartConfig,
} from './autoCustomerAddToCart';

/**
 * Configuração geral para autoTracks
 */
export interface IAutoTrackerFactoryConfig {
  enableAll?: boolean;
  globalDebounceMs?: number;
  globalThrottleMs?: number;
  globalRetryAttempts?: number;
  globalRetryDelay?: number;
  enableLogging?: boolean;
}

/**
 * Estatísticas do factory
 */
export interface IAutoTrackerFactoryStats {
  totalAutoTrackers: number;
  activeAutoTrackers: number;
  autoTrackerTypes: string[];
  globalConfig: IAutoTrackerFactoryConfig;
}

/**
 * Factory para gerenciar autoTracks
 */
export class AutoTrackerFactory {
  private config: GomarkePixelConfig;
  private sessionId: string;
  private version: string;
  private visitorId: string = '';
  private factoryConfig: IAutoTrackerFactoryConfig;
  private autoTrackers: Map<string, BaseAutoTracker> = new Map();
  private trackConversion: TrackConversionFn | null = null;

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    factoryConfig: IAutoTrackerFactoryConfig = {}
  ) {
    this.config = config;
    this.sessionId = sessionId;
    this.version = version;
    this.factoryConfig = {
      enableAll: true,
      globalDebounceMs: 300,
      globalThrottleMs: 1000,
      globalRetryAttempts: 3,
      globalRetryDelay: 1000,
      enableLogging: config.debug,
      ...factoryConfig,
    };
  }

  /**
   * Define o callback de track do ConversionEngine (chamar após initConversionEngine).
   */
  public setTrackConversion(fn: TrackConversionFn): void {
    this.trackConversion = fn;
  }

  /**
   * Cria e configura AutoAddToCart
   */
  public async createAddToCartTracker(
    autoConfig: IAutoAddToCartConfig
  ): Promise<AutoAddToCartTracker> {
    const key = 'addToCart';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(key) as unknown as AutoAddToCartTracker;
    }

    const tracker = new AutoAddToCartTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId,
      this.trackConversion ?? undefined
    );

    // Aplica configurações globais
    const mergedConfig = this.mergeConfigs(autoConfig);

    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log(`AutoAddToCart criado e inicializado`);
    return tracker;
  }

  /**
   * Cria e configura AutoInitiateCheckout
   */
  public async createInitiateCheckoutTracker(
    autoConfig: IAutoInitiateCheckoutConfig
  ): Promise<AutoInitiateCheckoutTracker> {
    const key = 'initiateCheckout';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(
        key
      ) as unknown as AutoInitiateCheckoutTracker;
    }

    const tracker = new AutoInitiateCheckoutTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId,
      this.trackConversion ?? undefined
    );

    // Aplica configurações globais
    const mergedConfig = this.mergeConfigs(autoConfig);

    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log(`AutoInitiateCheckout criado e inicializado`);
    return tracker;
  }

  /**
   * Cria e configura AutoAddPaymentInfo
   */
  public async createAddPaymentInfoTracker(
    autoConfig: IAutoAddPaymentInfoConfig
  ): Promise<AutoAddPaymentInfoTracker> {
    const key = 'addPaymentInfo';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(
        key
      ) as unknown as AutoAddPaymentInfoTracker;
    }

    const tracker = new AutoAddPaymentInfoTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId,
      this.trackConversion ?? undefined
    );

    const mergedConfig = this.mergeConfigs(autoConfig);

    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log(`AutoAddPaymentInfo criado e inicializado`);
    return tracker;
  }

  /**
   * Cria e configura AutoCustomerTracker (captura de dados do cliente)
   */
  public async createCustomerTracker(
    autoConfig: IAutoCustomerTrackerConfig
  ): Promise<AutoCustomerTracker> {
    const key = 'customer';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(key) as unknown as AutoCustomerTracker;
    }

    const tracker = new AutoCustomerTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId
    );

    const mergedConfig = this.mergeConfigs(autoConfig);
    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log('AutoCustomerTracker criado e inicializado');
    return tracker;
  }

  /**
   * Cria e configura AutoCustomerAddToCart (disparo automático de ADD_TO_CART)
   */
  public async createCustomerAddToCartTracker(
    autoConfig: IAutoCustomerAddToCartConfig
  ): Promise<AutoCustomerAddToCartTracker> {
    const key = 'customerAddToCart';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(
        key
      ) as unknown as AutoCustomerAddToCartTracker;
    }

    const tracker = new AutoCustomerAddToCartTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId,
      this.trackConversion ?? undefined
    );

    const mergedConfig = this.mergeConfigs(autoConfig);
    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log('AutoCustomerAddToCart criado e inicializado');
    return tracker;
  }

  /**
   * Cria e configura AutoLead
   */
  public async createLeadTracker(
    autoConfig: IAutoLeadConfig
  ): Promise<AutoLeadTracker> {
    const key = 'lead';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(key) as unknown as AutoLeadTracker;
    }

    const tracker = new AutoLeadTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId,
      this.trackConversion ?? undefined
    );

    // Aplica configurações globais
    const mergedConfig = this.mergeConfigs(autoConfig);

    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log(`AutoLead criado e inicializado`);
    return tracker;
  }

  /**
   * Cria e configura AutoRemoveToCart
   */
  public async createRemoveToCartTracker(
    autoConfig: IAutoRemoveToCartConfig
  ): Promise<AutoRemoveToCartTracker> {
    const key = 'removeToCart';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(key) as unknown as AutoRemoveToCartTracker;
    }

    const tracker = new AutoRemoveToCartTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId,
      this.trackConversion ?? undefined
    );

    const mergedConfig = this.mergeConfigs(autoConfig);

    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log(`AutoRemoveToCart criado e inicializado`);
    return tracker;
  }

  /**
   * Cria e configura AutoViewContent
   */
  public async createViewContentTracker(
    autoConfig: IAutoViewContentConfig
  ): Promise<AutoViewContentTracker> {
    const key = 'viewContent';

    if (this.autoTrackers.has(key)) {
      return this.autoTrackers.get(key) as unknown as AutoViewContentTracker;
    }

    const tracker = new AutoViewContentTracker(
      this.config,
      this.sessionId,
      this.version,
      this.visitorId,
      this.trackConversion ?? undefined
    );

    const mergedConfig = this.mergeConfigs(autoConfig);

    await tracker.initialize(mergedConfig);
    this.autoTrackers.set(key, tracker);

    this.log(`AutoViewContent criado e inicializado`);
    return tracker;
  }

  /**
   * Obtém um autoTracker existente
   */
  public getAutoTracker(type: string): BaseAutoTracker | undefined {
    return this.autoTrackers.get(type);
  }

  /**
   * Obtém todos os autoTrackers
   */
  public getAllAutoTrackers(): Map<string, BaseAutoTracker> {
    return new Map(this.autoTrackers);
  }

  /**
   * Para um autoTracker específico
   */
  public stopAutoTracker(type: string): boolean {
    const tracker = this.autoTrackers.get(type);
    if (tracker) {
      tracker.stop();
      this.autoTrackers.delete(type);
      this.log(`AutoTracker ${type} parado`);
      return true;
    }
    return false;
  }

  /**
   * Para todos os autoTrackers
   */
  public stopAllAutoTrackers(): void {
    this.autoTrackers.forEach((tracker, type) => {
      tracker.stop();
      this.log(`AutoTracker ${type} parado`);
    });
    this.autoTrackers.clear();
    this.log('Todos os autoTrackers foram parados');
  }

  /**
   * Reconstrói todos os autoTrackers
   */
  public async rebuildAllAutoTrackers(): Promise<void> {
    const rebuildPromises = Array.from(this.autoTrackers.values()).map(
      tracker => tracker.rebuild()
    );

    await Promise.all(rebuildPromises);
    this.log('Todos os autoTrackers foram reconstruídos');
  }

  /**
   * Atualiza configuração global
   */
  public updateFactoryConfig(
    newConfig: Partial<IAutoTrackerFactoryConfig>
  ): void {
    this.factoryConfig = { ...this.factoryConfig, ...newConfig };
    this.log('Configuração do factory atualizada', newConfig);
  }

  /**
   * Atualiza o sessionId (ex.: após init do Core Engine)
   */
  public updateSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    this.autoTrackers.forEach(t => t.stop());
    this.autoTrackers.clear();
  }

  /**
   * Atualiza o visitorId (ex.: após init do Core Engine)
   */
  public updateVisitorId(visitorId: string): void {
    this.visitorId = visitorId;
    this.autoTrackers.forEach(t => t.stop());
    this.autoTrackers.clear();
  }

  /**
   * Atualiza configuração de um autoTracker específico
   */
  public async updateAutoTrackerConfig(
    type: string,
    newConfig: any
  ): Promise<boolean> {
    const tracker = this.autoTrackers.get(type);
    if (tracker) {
      // Para e recria o tracker com nova configuração
      tracker.stop();
      this.autoTrackers.delete(type);

      // Recria com nova configuração
      switch (type) {
        case 'addToCart':
          await this.createAddToCartTracker(newConfig);
          break;
        case 'initiateCheckout':
          await this.createInitiateCheckoutTracker(newConfig);
          break;
        case 'addPaymentInfo':
          await this.createAddPaymentInfoTracker(newConfig);
          break;
        case 'customer':
          await this.createCustomerTracker(newConfig);
          break;
        case 'customerAddToCart':
          await this.createCustomerAddToCartTracker(newConfig);
          break;
        case 'lead':
          await this.createLeadTracker(newConfig);
          break;
        case 'removeToCart':
          await this.createRemoveToCartTracker(newConfig);
          break;
        case 'viewContent':
          await this.createViewContentTracker(newConfig);
          break;
        default:
          this.log(`Tipo de autoTracker desconhecido: ${type}`);
          return false;
      }

      return true;
    }
    return false;
  }

  /**
   * Obtém estatísticas do factory
   */
  public getFactoryStats(): IAutoTrackerFactoryStats {
    const activeTrackers = Array.from(this.autoTrackers.values()).filter(
      tracker => tracker.isActive()
    );

    return {
      totalAutoTrackers: this.autoTrackers.size,
      activeAutoTrackers: activeTrackers.length,
      autoTrackerTypes: Array.from(this.autoTrackers.keys()),
      globalConfig: { ...this.factoryConfig },
    };
  }

  /**
   * Obtém estatísticas detalhadas de todos os autoTrackers
   */
  public getDetailedStats(): Record<string, any> {
    const stats: Record<string, any> = {};

    this.autoTrackers.forEach((tracker, type) => {
      stats[type] = {
        isActive: tracker.isActive(),
        baseStats: tracker.getStats(),
      };

      // Estatísticas específicas por tipo
      if (type === 'addToCart' && 'getAddToCartStats' in tracker) {
        stats[type].specificStats = (tracker as any).getAddToCartStats();
      } else if (type === 'initiateCheckout' && 'getCheckoutStats' in tracker) {
        stats[type].specificStats = (tracker as any).getCheckoutStats();
      } else if (
        type === 'addPaymentInfo' &&
        'getPaymentInfoStats' in tracker
      ) {
        stats[type].specificStats = (tracker as any).getPaymentInfoStats();
      } else if (type === 'customer' && 'getCustomerStats' in tracker) {
        stats[type].specificStats = (tracker as any).getCustomerStats();
      } else if (
        type === 'customerAddToCart' &&
        'getAddToCartStats' in tracker
      ) {
        stats[type].specificStats = (tracker as any).getAddToCartStats();
      } else if (type === 'lead' && 'getLeadStats' in tracker) {
        stats[type].specificStats = (tracker as any).getLeadStats();
      } else if (type === 'removeToCart' && 'getRemoveToCartStats' in tracker) {
        stats[type].specificStats = (tracker as any).getRemoveToCartStats();
      } else if (type === 'viewContent' && 'getViewContentStats' in tracker) {
        stats[type].specificStats = (tracker as any).getViewContentStats();
      }
    });

    return stats;
  }

  /**
   * Verifica se um autoTracker está ativo
   */
  public isAutoTrackerActive(type: string): boolean {
    const tracker = this.autoTrackers.get(type);
    return tracker ? tracker.isActive() : false;
  }

  /**
   * Lista todos os autoTrackers ativos
   */
  public getActiveAutoTrackers(): string[] {
    return Array.from(this.autoTrackers.entries())
      .filter(([_, tracker]) => tracker.isActive())
      .map(([type, _]) => type);
  }

  /**
   * Mergeia configurações globais com configurações específicas
   */
  private mergeConfigs(autoConfig: any): any {
    return {
      ...autoConfig,
      debounceMs: autoConfig.debounceMs ?? this.factoryConfig.globalDebounceMs,
      throttleMs: autoConfig.throttleMs ?? this.factoryConfig.globalThrottleMs,
      retryAttempts:
        autoConfig.retryAttempts ?? this.factoryConfig.globalRetryAttempts,
      retryDelay: autoConfig.retryDelay ?? this.factoryConfig.globalRetryDelay,
      debug: autoConfig.debug ?? this.factoryConfig.enableLogging,
    };
  }

  /**
   * Log de eventos
   */
  private log(message: string, data?: any): void {
    if (this.factoryConfig.enableLogging) {
      console.log(`[AutoTrackerFactory] ${message}`, data);
    }
  }
}
